import useMessageTemplates from "@/composables/useMessageTemplates";
import ChannelInterface from "@/interfaces/channel.interface";
import { PaginatedResponseInterface } from "@/interfaces/paginatedResponse.interface";
import { TemplateInterface } from "@/interfaces/template.interface";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
import { CrudGetManyResponse } from "@/types/crudResponse.type";
import { ProviderType } from "@/types/provider.type";
import { computed, Ref, ref, watch } from "@vue/composition-api";
import { BTable } from "bootstrap-vue";

const { getLanguageFromShortCode } = useMessageTemplates();
export default function useTemplatesList() {
    const refTable: Ref<BTable | null> = ref(null);
    const tableColumns = [
        {
            key: "status",
            label: i18n.t("settings.whatsapp-templates.template.status"),
            sortable: true,
        },
        {
            key: "title",
            label: i18n.t("settings.whatsapp-templates.template.title"),
            sortable: true,
        },
        {
            key: "content",
            label: i18n.t("settings.whatsapp-templates.template.content"),
            sortable: true,
        },
        {
            key: "language",
            label: i18n.t("settings.whatsapp-templates.template.language"),
            sortable: true,
            formatter: (value: string) => {
                return getLanguageFromShortCode(value); // Transform the shortcode to a readable country
            },
        },
        {
            key: "category",
            label: i18n.t("settings.whatsapp-templates.template.category"),
            sortable: true,
        },
        {
            key: "channels",
            label: i18n.t("settings.whatsapp-templates.template.channels"),
        },
        {
            key: "submittedAt",
            label: i18n.t("settings.whatsapp-templates.template.submitted-at"),
            sortable: true,
        },
        { key: "actions", label: i18n.t("settings.whatsapp-templates.template.actions") },
    ];
    const perPage = ref(10);
    const total = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const sortBy = ref("submittedAt");
    const isSortDirDesc = ref(true);
    const isLoading = ref(true);
    const filters: Ref<{ status: string[]; category: string[]; channels: ChannelInterface[] }> = ref({
        status: [],
        category: [],
        channels: [],
    });

    const dataMeta = computed(() => {
        const localItemsCount = refTable.value ? refTable.value.localItems.length : 0;

        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: total.value,
        };
    });

    const refetchData = () => {
        refTable.value?.refresh();
    };

    // NOTE: Using anonymous functions for the filter values because of how object refs interact with watchers
    watch(
        [
            currentPage,
            perPage,
            searchQuery,
            () => filters.value.status,
            () => filters.value.category,
            () => filters.value.channels,
        ],
        () => {
            if (searchQuery.value.length > 0 && searchQuery.value.length < 2) {
                return;
            }

            refetchData();
        }
    );

    const read = (_ctx: Record<string, unknown>, callback: CallableFunction) => {
        getManyTemplates<PaginatedResponseInterface<TemplateInterface>>({
            fields: "title,content,name,submittedAt,status,language,category,wabaId",
            s: {
                $and: [
                    {
                        $or: [
                            {
                                $or: [
                                    {
                                        "channels.provider": {
                                            $eq: ProviderType.WHATSAPP,
                                        },
                                    },
                                    {
                                        "channels.provider": {
                                            $eq: ProviderType.MESSAGEBIRD,
                                        },
                                    },
                                    {
                                        "channels.provider": {
                                            $eq: ProviderType.BIRD,
                                        },
                                    },
                                ],
                            },
                            { "channels.id": { $isnull: true } },
                        ],
                    },
                    {
                        $or: [{ title: { $cont: searchQuery.value } }, { content: { $cont: searchQuery.value } }],
                    },
                    filters.value.status.length > 0 && {
                        status: {
                            $in: filters.value.status,
                        },
                    },
                    filters.value.category.length > 0 && {
                        category: {
                            $in: filters.value.category,
                        },
                    },
                    filters.value.channels.length > 0 && {
                        "channels.id": {
                            $in: filters.value.channels,
                        },
                    },
                ],
            },
            limit: perPage.value,
            page: currentPage.value,
            sort: `${sortBy.value},${isSortDirDesc.value === true ? "DESC" : "ASC"}`,
            join: "channels||name,provider",
        })
            .then((response) => {
                callback(response.data);
                total.value = response.total;
            })
            .finally(() => (isLoading.value = false));
    };

    const getTranslatedError = (message: string | undefined) => {
        message = message || "unknown";

        // Filter out error message if it comes directly from Messagebird
        const regexResult = /^Bad request \((?<error>.+)\)$/.exec(message);

        if (regexResult?.groups?.error) {
            message = regexResult.groups.error;
        }

        const translatedMessage = i18n.t(
            `settings.templates.whatsapp.errors.${message.toLowerCase().replace(/ /g, "-")}`
        );

        // don't give the error if we don't know what it is
        return translatedMessage.startsWith("settings.templates.whatsapp.errors.")
            ? i18n.t(`settings.templates.whatsapp.errors.unknown`)
            : translatedMessage;
    };

    const getManyTemplates = <T extends CrudGetManyResponse<TemplateInterface> = TemplateInterface[]>(
        params: any
    ): Promise<T> => {
        return axios
            .get("templates", {
                params,
            })
            .then((response) => response.data);
    };

    return {
        read,
        refetchData,
        getManyTemplates,
        getTranslatedError,
        tableColumns,
        perPage,
        currentPage,
        total,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refTable,
        isLoading,
        filters,
    };
}
